import type { Checklist } from '@cognite/apm-client';
import type { SchedulerCreatedNode } from '@infield/features/checklist/checklist-scheduler/types';

export type CreatePlannedChecklistsFn = (
  checklists: Checklist[]
) => Promise<SchedulerCreatedNode[]>;

export type CreatedAndFailedChecklists = {
  createdChecklists: Checklist[];
  failedChecklists: Checklist[];
};

/**
 * Creates given checklists and returns the created and failed checklists in two separate arrays.
 *
 * @param checklists - An array of checklists to be created.
 * @param createPlannedChecklists - Function to create planned checklists.
 * @param setProgress - Function to set the progress (percent) of the checklist creation.
 * @returns An object containing two arrays: `createdChecklists` and `failedChecklists`.
 */

export const createChecklistsSequentially = async (
  checklists: Checklist[],
  createPlannedChecklists: CreatePlannedChecklistsFn,
  setProgress: (progress: number) => void
): Promise<CreatedAndFailedChecklists> => {
  const createdChecklists: Checklist[] = [];
  const failedChecklists: Checklist[] = [];

  for (const checklist of checklists) {
    try {
      const checklistsCreatedResult = await createPlannedChecklists([
        checklist,
      ]);
      if (checklistsCreatedResult && checklistsCreatedResult.length > 0) {
        createdChecklists.push(checklist);
        const progress = Math.round(
          (createdChecklists.length / checklists.length) * 100
        );
        setProgress(progress);
      }
    } catch (error) {
      failedChecklists.push(checklist);
    }
  }

  return { failedChecklists, createdChecklists };
};
